import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Home Collections Preview — one representative image per category.
 * Image is CLICKABLE (opens the collection page) but has NO text overlay on the image itself.
 * Category name + description sit BELOW the image for clarity.
 */
export default function Collections() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/collections`).then((r) => setItems(r.data || [])).catch(() => {}); }, []);

  return (
    <section className="relative bg-white py-20 md:py-32 border-t border-slate-100">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-4">{t.collections.eyebrow}</div>
            <h2 className="font-display font-medium text-slate-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight">
              {t.collections.title}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <Link to="/products" className="inline-flex items-center gap-2 text-slate-700 hover:text-[var(--steel)] text-xs font-grotesk font-semibold uppercase tracking-[0.2em] transition-colors">
              View all products <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className="card-hover group rounded-xl overflow-hidden bg-white border border-slate-200"
              data-testid={`collection-${c.slug}`}
            >
              <Link
                to={`/collections/${c.slug}`}
                className="block relative aspect-[4/5] overflow-hidden bg-slate-100"
                aria-label={c.title}
                data-testid={`collection-image-${c.slug}`}
              >
                {c.image ? (
                  <img
                    src={c.image}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[var(--steel-soft)] to-slate-100">
                    <span className="font-display font-medium text-slate-400 text-xl">Ramdev Steel</span>
                  </div>
                )}
              </Link>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-slate-900 text-lg md:text-xl leading-tight tracking-tight mb-2">{c.title}</h3>
                <p className="text-slate-600 text-[13px] leading-relaxed mb-4 line-clamp-2">{c.description}</p>
                <Link
                  to={`/collections/${c.slug}`}
                  className="inline-flex items-center gap-1.5 text-[var(--steel)] hover:text-[var(--steel-hover)] text-[11px] font-grotesk font-semibold uppercase tracking-[0.2em] transition-colors"
                  data-testid={`collection-view-${c.slug}`}
                >
                  {t.collections.view} <ArrowUpRight size={12} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
